import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useFetchAuthorisation, useFetchUserData } from "../../utils/AppFunctions";
import config from "../../app-config";
import "react-toastify/dist/ReactToastify.css";
import AdminLayout from "../../layouts/AdminLayout";
import { approve_claim, create_new_claim, delete_claim, fetch_claim_by_id, mark_claim_as_reimbursed, process_payment, reject_claim, update_claim } from "../../api/claimsAPI";
import notify from "../../utils/IANotification";
import ModalLayout from "../../layouts/ModalLayout";
import { fetch_all_users_admin } from "../../api/usersAPI";
import Select from "react-select"
import { fetch_all_accounts } from "../../api/accountsAPI";
import FileUploadField from "../../components/ui/FileUploadField";
import { MdDelete, MdDownload, MdOpenInNew, MdArrowBack, MdSave } from "react-icons/md";
import SubNavbar from "../../components/ui/SubNavbar";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";

export default function ClaimsFormPage() {
  const navigate = useNavigate();
  useFetchAuthorisation("user");
  useFetchUserData();

  let claims_id = new URLSearchParams(window.location.search).get("claims_id") || "new";
  const [activeTab, setActiveTab] = useState("general");
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)
  const [previewModalVisible, setPreviewModalVisible] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)

  const [claimsData, setClaimsData] = useState({
    account_id: "",
    claim_type: "",
    category_id: "",
    user_id: "",
    description: "",
    claim_value: "",
    currency: "",
    currency_rate: "",
    claim_date: "",
    files: []
  })
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [accounts, setAccountData] = useState([])

  const tabs = [
    { id: 'general', label: 'Claim Details' },
    { id: 'attachments', label: 'Receipts & Attachments' },
  ]

  const fetchClaimsById = async () => {
    if (claims_id === "new") return
    let { data, error } = await fetch_claim_by_id(claims_id)
    if (error) return console.error(error)
    setClaimsData(data)
  }

  const fetchAccounts = async () => {
    setLoading(true)
    const { data, error, message } = await fetch_all_accounts()
    if (error) console.log("Error fetching accounts:", message)
    setAccountData(data || [])
    setLoading(false)
  }

  const fetchPlatformUsers = async () => {
    setLoading(true)
    const data = await fetch_all_users_admin()
    if (data?.status === 200) {
      setUsers(data.data || [])
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchAccounts()
    fetchPlatformUsers()
    fetchClaimsById()
  }, [])

  const submitClaim = async () => {
    let formData = new FormData();
    for (let key in claimsData) {
      formData.append(key, claimsData[key]);
    }
    for (let i = 0; i < (claimsData?.files?.length || 0); i++) {
      formData.append('files', claimsData?.files[i]);
    }
    if (claims_id !== "new") return
    let { data, message, error } = await create_new_claim(formData)
    if (error) return notify({ type: "error", message: `Error creating claim: ${message}` })
    notify({ type: "success", message: "Claim created successfully" })
    navigate(`/admin/claims/details?claims_id=${data.id}`)
  }

  const updateClaim = async () => {
    if (claims_id === "new") return
    let formData = new FormData();
    for (let key in claimsData) {
      formData.append(key, claimsData[key]);
    }
    for (let i = 0; i < (claimsData?.files?.length || 0); i++) {
      formData.append('files', claimsData.files[i]);
    }
    let { error, message } = await update_claim(claims_id, formData)
    if (error) return notify({ type: "error", message: `Error updating claim: ${message}` })
    await fetchClaimsById()
    notify({ type: "success", message: "Claim updated successfully" })
  }

  const deleteClaim = async () => {
    if (claims_id === "new") return
    let { error, message } = await delete_claim(claims_id)
    if (error) return notify({ type: "error", message: `Error deleting claim: ${message}` })
    notify({ type: "success", message: "Claim deleted successfully" })
    navigate(`/admin/claims/`)
  }

  const approveClaim = async () => {
    if (claims_id === "new") return
    let { error, message } = await approve_claim(claims_id)
    if (error) return notify({ type: "error", message: `Error approving claim: ${message}` })
    await fetchClaimsById()
    notify({ type: "success", message: "Claim approved successfully" })
  }

  const rejectClaim = async () => {
    if (claims_id === "new") return
    let { error, message } = await reject_claim(claims_id)
    if (error) return notify({ type: "error", message: `Error rejecting claim: ${message}` })
    await fetchClaimsById()
    notify({ type: "success", message: "Claim rejected successfully" })
  }

  const processClaimPayment = async () => {
    if (claims_id === "new") return
    let { error, message } = await process_payment(claims_id)
    if (error) return notify({ type: "error", message: `Error processing claim payment: ${message}` })
    await fetchClaimsById()
    notify({ type: "success", message: "Claim payment processed successfully" })
  }

  const markClaimAsReimbursed = async () => {
    if (claims_id === "new") return
    let { error, message } = await mark_claim_as_reimbursed(claims_id)
    if (error) return notify({ type: "error", message: `Error marking claim as reimbursed: ${message}` })
    await fetchClaimsById()
    notify({ type: "success", message: "Claim marked as reimbursed successfully" })
  }

  const isEditable = claimsData?.claim_status === "pending" || claimsData?.claim_status === "rejected" || claims_id === "new"

  return (
    <AdminLayout
      navSelected={"Claims"}
      headerText={claims_id === "new" ? "New Expense Claim" : "Edit Expense Claim"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/claims')}>
              Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900">
              {claims_id === "new" ? "Submit Claim" : `Claim #${claims_id}`}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {claims_id !== "new" && isEditable && (
              <Button variant="danger" icon={MdDelete} onClick={() => setShowDeleteConfirmationModal(true)}>
                Delete
              </Button>
            )}
            {(claimsData?.claim_status === "pending" || claimsData?.claim_status === "rejected") && (
              <>
                <Button variant="primary" onClick={approveClaim}>
                  Approve
                </Button>
                <Button variant="outline" onClick={rejectClaim}>
                  Reject
                </Button>
              </>
            )}
            {claimsData?.claim_status === "approved" && (
              <Button variant="primary" onClick={processClaimPayment}>
                Process Payment
              </Button>
            )}
            {claimsData?.claim_status === "process_payment" && (
              <Button variant="primary" onClick={markClaimAsReimbursed}>
                Mark Reimbursed
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (claims_id === "new") submitClaim()
              else updateClaim()
            }}>
              {claims_id === "new" ? "Submit" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <ModalLayout modalTitle="Delete Expense Claim"
        isVisible={showDeleteConfirmationModal} setIsVisible={setShowDeleteConfirmationModal}>
        <div className="space-y-4">
          <p className="text-xs text-gray-600">Are you sure you want to delete this expense claim?</p>
          <div className="flex justify-end gap-2">
            <Button variant="danger" icon={MdDelete} onClick={deleteClaim}>
              Confirm Delete
            </Button>
          </div>
        </div>
      </ModalLayout>

      {/* TAB 1: GENERAL CLAIM DETAILS */}
      {activeTab === "general" && (
        <Card title="Expense Claim Details" subtitle="Claimee, financial account, claim value, and expenditure type.">
          <div className="space-y-6">
            <div className="input-container">
              <label className="input-label font-semibold">Expenditure Description</label>
              <textarea
                className="textarea-input h-24"
                placeholder="e.g. Return flight and accommodation for mission trip..."
                disabled={!isEditable}
                value={claimsData.description || ""}
                onChange={(e) => setClaimsData({ ...claimsData, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="input-container">
                <label className="input-label font-semibold">Account Ledger</label>
                <Select
                  value={accounts.find(account => account.id === claimsData.account_id) || null}
                  onChange={(selectedOption) => setClaimsData({ ...claimsData, account_id: selectedOption.id })}
                  getOptionLabel={(e) => `${e.name}`}
                  getOptionValue={(e) => e.id}
                  options={accounts}
                  isDisabled={!isEditable}
                  placeholder="Select Account"
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Claim Type</label>
                <Select
                  value={claimsData.claim_type ? { value: claimsData.claim_type, label: claimsData.claim_type.charAt(0).toUpperCase() + claimsData.claim_type.slice(1) } : null}
                  onChange={(selectedOption) => setClaimsData({ ...claimsData, claim_type: selectedOption.value })}
                  options={[
                    { value: "food", label: "Food & Catering" },
                    { value: "transport", label: "Transport & Travel" },
                    { value: "accommodation", label: "Accommodation" },
                    { value: "stationery", label: "Stationery & Supplies" },
                    { value: "equipment", label: "Equipment" },
                    { value: "other", label: "Other Expense" },
                  ]}
                  isDisabled={!isEditable}
                  placeholder="Select Claim Type"
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Claimee (Applicant)</label>
                <Select
                  value={users.find(user => user.id == claimsData.user_id) || null}
                  onChange={(selectedOption) => setClaimsData({ ...claimsData, user_id: selectedOption.id })}
                  getOptionLabel={(e) => `${e.first_name} ${e.last_name}`}
                  getOptionValue={(e) => e.id}
                  options={users}
                  placeholder="Select User"
                  isDisabled={!isEditable}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="input-container">
                <label className="input-label font-semibold">Claim Value</label>
                <input
                  type="number"
                  step="0.01"
                  disabled={!isEditable}
                  className="text-input"
                  placeholder="100.00"
                  value={claimsData.claim_value || ""}
                  onChange={(e) => setClaimsData({ ...claimsData, claim_value: e.target.value })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Currency Code</label>
                <Select
                  value={accounts.find(account => account.id === claimsData.account_id)?.currency?.find(c => c.currency === claimsData.currency) || null}
                  onChange={(selectedOption) => setClaimsData({ ...claimsData, currency: selectedOption.currency, currency_rate: selectedOption.rate })}
                  getOptionLabel={(e) => e.currency}
                  getOptionValue={(e) => e.currency}
                  options={accounts.find(account => account.id === claimsData.account_id)?.currency || []}
                  placeholder="Currency"
                  isDisabled={!isEditable}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Exchange Rate</label>
                <input
                  type="number"
                  step="0.0001"
                  className="text-input"
                  disabled={!isEditable}
                  placeholder="1.00"
                  value={claimsData.currency_rate || ""}
                  onChange={(e) => setClaimsData({ ...claimsData, currency_rate: e.target.value })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Expenditure Date</label>
                <input
                  type="date"
                  className="text-input"
                  disabled={!isEditable}
                  value={claimsData.claim_date || ""}
                  onChange={(e) => setClaimsData({ ...claimsData, claim_date: e.target.value })}
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* TAB 2: RECEPTS & ATTACHMENTS */}
      {activeTab === "attachments" && (
        <div className="space-y-6">
          {isEditable && (
            <Card title="Upload Receipt Files" subtitle="Attach photos or PDF receipts supporting this expense.">
              <FileUploadField
                item_index={"1"}
                max_file_size={5}
                max_files={10}
                onFileSelected={(files) => setClaimsData({ ...claimsData, files: files })}
                onChange={() => { }}
              />
            </Card>
          )}

          <Card title="Attached Receipt Media" subtitle="View and download uploaded proof of purchase.">
            {claimsData.file_data && claimsData.file_data.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4">
                {claimsData.file_data.map((file, index) => (
                  <div key={index} className="aspect-square border border-gray-200 rounded-xl overflow-hidden relative bg-gray-50">
                    {file?.ext_file_path?.toLowerCase().endsWith(".jpg") || file?.ext_file_path?.toLowerCase().endsWith(".png") || file?.ext_file_path?.toLowerCase().endsWith(".jpeg") ? (
                      <img
                        src={`${config.endpoint}${file?.ext_file_path}`}
                        alt={`Uploaded ${index + 1}`}
                        className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-all"
                        onClick={() => { setPreviewFile(file); setPreviewModalVisible(true); }}
                      />
                    ) : (
                      <div
                        className="w-full h-full flex flex-col items-center justify-center p-3 text-center cursor-pointer hover:bg-gray-100"
                        onClick={() => { setPreviewFile(file); setPreviewModalVisible(true); }}
                      >
                        <span className="font-bold text-xs text-blue-600 uppercase">{file?.file_name?.split('.').pop() || 'File'}</span>
                        <span className="text-[10px] text-gray-500 truncate w-full mt-1">{file?.file_name || file?.ext_file_path}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic py-6 text-center">No receipt files attached.</p>
            )}
          </Card>
        </div>
      )}

      {/* Preview Modal */}
      <ModalLayout modalTitle={previewFile ? (previewFile.file_name || 'Receipt Preview') : 'Preview'} isVisible={previewModalVisible} setIsVisible={setPreviewModalVisible}>
        {previewFile ? (
          <div className="space-y-4">
            <div className="flex justify-end gap-2">
              <a href={`${config.endpoint}${previewFile.ext_file_path}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" icon={MdOpenInNew}>
                  Open New Tab
                </Button>
              </a>
              <Button
                variant="primary"
                size="sm"
                icon={MdDownload}
                onClick={() => {
                  const url = `${config.endpoint}${previewFile.ext_file_path}`;
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = previewFile.file_name || url.split('/').pop();
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }}
              >
                Download
              </Button>
            </div>

            <div className="min-h-[400px] border border-gray-200 rounded-xl p-2 flex items-center justify-center bg-gray-50">
              {previewFile.ext_file_path?.toLowerCase().endsWith('.pdf') ? (
                <iframe src={`${config.endpoint}${previewFile.ext_file_path}`} title={previewFile.file_name || 'pdf-preview'} className="w-full h-[70vh] border-none" />
              ) : (
                <img src={`${config.endpoint}${previewFile.ext_file_path}`} alt={previewFile.file_name || 'preview'} className="max-w-full max-h-[70vh] object-contain" />
              )}
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-400">No file selected.</p>
        )}
      </ModalLayout>
    </AdminLayout>
  )
}